/**
 * Maintenance Tab Component - HYB MASTER SHOP
 * 
 * Provides UI for managing:
 * 1. Maintenance Mode (admin-controlled) - toggle to show maintenance page to all users
 * 2. Offline Message - customizable message shown when app cannot connect to server
 */

import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    AlertCircle,
    Eye,
    EyeOff,
    Power,
    PowerOff,
    Loader2,
    Wrench,
    WifiOff,
    Save,
} from 'lucide-react';
import {
    fetchMaintenanceSettings,
    updateMaintenanceSettings,
} from '../api/admin';

// Default messages
const DEFAULT_MAINTENANCE_MESSAGE = 'ระบบกำลังปรับปรุง กรุณากลับมาใหม่ภายหลัง';
const DEFAULT_OFFLINE_MESSAGE = 'Application ยังไม่พร้อมใช้งานในขณะนี้ บริษัทกำลังปรับปรุงคุณภาพการให้บริการ กรุณาลองใช้งานใหม่อีกครั้งในเร็วๆ นี้';

export function MaintenanceTab() {
    const queryClient = useQueryClient();

    // Local state for form
    const [enabled, setEnabled] = useState(false);
    const [message, setMessage] = useState(DEFAULT_MAINTENANCE_MESSAGE);
    const [offlineMessage, setOfflineMessage] = useState(DEFAULT_OFFLINE_MESSAGE);

    // Password modal state
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [pendingEnabled, setPendingEnabled] = useState(false);

    // Query for fetching current settings
    const { data: maintenanceSettings, isLoading } = useQuery({
        queryKey: ['maintenanceSettings'],
        queryFn: fetchMaintenanceSettings,
        staleTime: 30000, // 30 seconds
    });

    // Update local state when server data is fetched
    useEffect(() => {
        if (maintenanceSettings) {
            setEnabled(maintenanceSettings.enabled);
            setMessage(maintenanceSettings.message || DEFAULT_MAINTENANCE_MESSAGE);
            setOfflineMessage(maintenanceSettings.offlineMessage || DEFAULT_OFFLINE_MESSAGE);
        }
    }, [maintenanceSettings]);

    // Mutation for updating maintenance settings
    const updateMutation = useMutation({
        mutationFn: updateMaintenanceSettings,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['maintenanceSettings'] });
            queryClient.invalidateQueries({ queryKey: ['appSettings'] });
            setShowPasswordModal(false);
            setPassword('');
            setEnabled(data.enabled);
            toast.success(
                data.enabled
                    ? 'เปิด Maintenance Mode สำเร็จ - แอปจะแสดงหน้า Maintenance ให้ผู้ใช้ทุกคน'
                    : 'ปิด Maintenance Mode สำเร็จ - แอปพร้อมใช้งานปกติ'
            );
        },
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.error || error?.message || 'เกิดข้อผิดพลาด';
            if (errorMessage.includes('password') || errorMessage.includes('Invalid')) {
                toast.error('รหัสผ่านไม่ถูกต้อง');
            } else {
                toast.error(`เกิดข้อผิดพลาด: ${errorMessage}`);
            }
        },
    });

    // Handle toggle click - show password modal
    const handleToggleClick = (newEnabled: boolean) => {
        setPendingEnabled(newEnabled);
        setShowPasswordModal(true);
    };

    // Handle password submit
    const handlePasswordSubmit = () => {
        if (!password.trim()) {
            toast.error('กรุณาใส่รหัสผ่าน');
            return;
        }

        updateMutation.mutate({
            enabled: pendingEnabled,
            message,
            offlineMessage,
            password,
        });
    };

    // Handle save messages (requires password for maintenance message)
    const handleSaveMessages = () => {
        // For now, show password modal to save all settings
        setPendingEnabled(enabled); // Keep current enabled state
        setShowPasswordModal(true);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                <span className="ml-2 text-gray-600 dark:text-gray-400">กำลังโหลดการตั้งค่า...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Maintenance Mode Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4">
                    <div className="flex items-center gap-3">
                        <Wrench className="w-6 h-6 text-white" />
                        <div>
                            <h3 className="text-lg font-semibold text-white">Maintenance Mode (ควบคุมโดย Admin)</h3>
                            <p className="text-sm text-orange-100">เปิด/ปิดโหมดปรับปรุงระบบสำหรับผู้ใช้งานทุกคน</p>
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-4">
                    {/* Status Badge */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {enabled ? (
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-sm font-medium">
                                    <PowerOff className="w-4 h-4" />
                                    <span>Maintenance Mode เปิดอยู่</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                                    <Power className="w-4 h-4" />
                                    <span>แอปทำงานปกติ</span>
                                </div>
                            )}
                        </div>

                        {/* Toggle Button */}
                        <button
                            onClick={() => handleToggleClick(!enabled)}
                            disabled={updateMutation.isPending}
                            className={`
                relative inline-flex h-8 w-14 items-center rounded-full transition-colors
                ${enabled ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600'}
                ${updateMutation.isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
                        >
                            <span
                                className={`
                  inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform
                  ${enabled ? 'translate-x-7' : 'translate-x-1'}
                `}
                            />
                        </button>
                    </div>

                    {/* Warning Message */}
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-amber-800 dark:text-amber-300">
                                <p className="font-medium mb-1">เมื่อเปิดโหมดนี้:</p>
                                <ul className="list-disc list-inside space-y-1 text-amber-700 dark:text-amber-400">
                                    <li>แอปจะแสดงหน้า Maintenance ให้ผู้ใช้ทุกคน</li>
                                    <li>ผู้ใช้จะไม่สามารถใช้งานแอปได้จนกว่าจะปิดโหมดนี้</li>
                                    <li>ข้อความด้านล่างจะแสดงให้ผู้ใช้เห็น</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Maintenance Message Input */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            ข้อความแสดงผล (Maintenance Mode)
                        </label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={3}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            placeholder={DEFAULT_MAINTENANCE_MESSAGE}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            ข้อความนี้จะแสดงในหน้า Maintenance เมื่อเปิด Maintenance Mode
                        </p>
                    </div>
                </div>
            </div>

            {/* Offline Message Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-6 py-4">
                    <div className="flex items-center gap-3">
                        <WifiOff className="w-6 h-6 text-white" />
                        <div>
                            <h3 className="text-lg font-semibold text-white">ข้อความเมื่อเชื่อมต่อไม่ได้ (อัตโนมัติ)</h3>
                            <p className="text-sm text-blue-100">ข้อความที่แสดงเมื่อแอปไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้</p>
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-4">
                    {/* Info Message */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-500 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-800 dark:text-blue-300">
                                <p>
                                    ข้อความนี้จะแสดงโดยอัตโนมัติเมื่อแอปไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้
                                    (เช่น Internet ขัดข้อง, Server down)
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Offline Message Input */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            ข้อความแสดงผล (Offline/Error)
                        </label>
                        <textarea
                            value={offlineMessage}
                            onChange={(e) => setOfflineMessage(e.target.value)}
                            rows={4}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder={DEFAULT_OFFLINE_MESSAGE}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            ข้อความนี้จะ cache ไว้ในแอป เพื่อใช้แสดงเมื่อไม่มีการเชื่อมต่อ
                        </p>
                    </div>
                </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
                <button
                    onClick={handleSaveMessages}
                    disabled={updateMutation.isPending}
                    className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {updateMutation.isPending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                        <Save className="w-5 h-5" />
                    )}
                    <span>บันทึกการตั้งค่า</span>
                </button>
            </div>

            {/* Password Confirmation Modal */}
            {showPasswordModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                🔐 ยืนยันการดำเนินการ
                            </h3>
                        </div>

                        <div className="p-6 space-y-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                กรุณาใส่รหัสผ่าน Admin เพื่อยืนยัน{pendingEnabled ? 'การเปิด' : 'การปิด'} Maintenance Mode
                            </p>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    รหัสผ่าน
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handlePasswordSubmit();
                                            }
                                        }}
                                        className="w-full p-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="กรุณาใส่รหัสผ่าน"
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="w-5 h-5" />
                                        ) : (
                                            <Eye className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowPasswordModal(false);
                                    setPassword('');
                                }}
                                disabled={updateMutation.isPending}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                ยกเลิก
                            </button>
                            <button
                                onClick={handlePasswordSubmit}
                                disabled={updateMutation.isPending || !password.trim()}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {updateMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : null}
                                <span>ยืนยัน</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MaintenanceTab;
